import { useCallback, useEffect, useState } from 'react';
import { shopsService } from '../services/api';

/**
 * Each shop's IANA timezone, read once from its settings and shared across
 * pages. Appointment times are shop-local wall clock, so screens that show or
 * group them need the shop's zone, not the viewer's.
 *
 * Returns a lookup that yields `undefined` until a zone has loaded (or when
 * it cannot be read), which the shopTime helpers treat as "browser zone" — the
 * previous behaviour, correct whenever the viewer sits in the shop's zone.
 */
const zones = new Map<string, string>();
const inFlight = new Map<string, Promise<void>>();

/**
 * Records a zone already in hand — the shop read (`GET /shops/slug/:slug`)
 * carries it — so screens of the selected shop have it on first render
 * instead of loading the settings and then rendering, or fetching, twice.
 */
export function primeShopTimezone(shopId: string, timeZone: string | undefined) {
  if (timeZone) zones.set(shopId, timeZone);
}

function load(shopId: string): Promise<void> {
  const pending =
    inFlight.get(shopId) ??
    shopsService
      .getShopSettings(shopId)
      .then((settings) => {
        if (settings?.timezone) zones.set(shopId, settings.timezone);
      })
      .catch(() => undefined)
      .finally(() => inFlight.delete(shopId));
  inFlight.set(shopId, pending);
  return pending;
}

export function useShopTimezones(shopIds: (string | null | undefined)[]) {
  const [version, setVersion] = useState(0);
  const key = Array.from(new Set(shopIds.filter((id): id is string => Boolean(id))))
    .sort()
    .join(',');

  useEffect(() => {
    const missing = key ? key.split(',').filter((id) => !zones.has(id)) : [];
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map(load)).then(() => {
      if (!cancelled) setVersion((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  // `version` makes the lookup a new function once zones arrive, so memoised
  // values that depend on it recompute.
  return useCallback(
    (shopId?: string | null): string | undefined => (shopId ? zones.get(shopId) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version]
  );
}
